import { logSpace, linearRegression, computeSSE, computeRSquared } from '../utils/math';
import { computePoissonLogL } from './diagnostics';
import { computeExpectedCurveCounts, computeIncrementalMetrics } from './curveUtils';
import { filterDiscoveryCounts } from '../series/discoverySeries';

export const gompertzCurve = (params, x) => params.a * Math.exp(-params.b * Math.exp(-params.c * x));

export const fitGompertz = (points, options = {}) => {
  if (!points || points.length < 3) return null;
  const xs = points.map(point => point.x);
  const ys = points.map(point => point.y);
  const maxY = Math.max(...ys);
  if (!Number.isFinite(maxY) || maxY <= 0) return null;

  const {
    discoveryCounts,
    modelStartYear,
    fitMethod = 'cumulative',
  } = options;

  const minA = maxY * 1.01;
  const maxA = maxY * 10;
  const candidates = logSpace(minA, maxA, 36);
  let best = null;

  const evaluate = (a) => {
    const xVals = [];
    const wVals = [];
    for (let i = 0; i < points.length; i += 1) {
      const y = points[i].y;
      const x = points[i].x;
      if (!(y > 0 && y < a)) continue;
      const ratio = y / a;
      if (!(ratio > 0 && ratio < 1)) continue;
      const inner = -Math.log(ratio);
      if (!(inner > 0)) continue;
      const w = Math.log(inner);
      if (!Number.isFinite(w)) continue;
      xVals.push(x);
      wVals.push(w);
    }

    if (xVals.length < 3) return;
    const lr = linearRegression(xVals, wVals);
    if (!lr || lr.slope >= 0) return;
    const c = -lr.slope;
    const b = Math.exp(lr.intercept);
    if (!Number.isFinite(b) || !Number.isFinite(c) || b <= 0 || c <= 0) return;

    const predicted = xs.map(x => gompertzCurve({ a, b, c }, x));
    const sse = computeSSE(ys, predicted);
    const rSquared = computeRSquared(ys, predicted);

    let logL = null;
    if (fitMethod === 'poisson' && discoveryCounts && Number.isFinite(modelStartYear)) {
      const expected = computeExpectedCurveCounts(gompertzCurve, { a, b, c }, discoveryCounts, modelStartYear);
      const observed = filterDiscoveryCounts(discoveryCounts, modelStartYear);
      logL = expected ? computePoissonLogL(observed, expected) : null;
      if (!Number.isFinite(logL)) return;
    }

    if (fitMethod === 'poisson') {
      if (!best || logL > best.logL) {
        best = { params: { a, b, c }, rSquared, sse, logL };
      }
    } else if (!best || sse < best.sse) {
      best = { params: { a, b, c }, rSquared, sse };
    }
  };

  candidates.forEach(evaluate);
  if (!best) return null;

  const refineMin = Math.max(minA, best.params.a / 1.5);
  const refineMax = best.params.a * 1.5;
  const refinedCandidates = logSpace(refineMin, refineMax, 24);
  refinedCandidates.forEach(evaluate);

  const result = { ...best, fitMethod };
  if (discoveryCounts && Number.isFinite(modelStartYear)) {
    const expected = computeExpectedCurveCounts(gompertzCurve, result.params, discoveryCounts, modelStartYear);
    const observed = filterDiscoveryCounts(discoveryCounts, modelStartYear);
    const metrics = expected ? computeIncrementalMetrics(observed, expected) : null;
    if (metrics && Number.isFinite(metrics.rSquared)) {
      result.rSquaredCumulative = result.rSquared;
      result.rSquared = metrics.rSquared;
      result.incrementalSse = metrics.sse;
    }
  }

  return result;
};
